import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, BookOpen, CalendarDays, Filter, Search } from "lucide-react";
import { Badge } from "src/components/ui/Badge";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { ROUTES } from "src/routes/RouteConfig";
import { getUnitMasteryCounts, masteryStudents, masteryStatusLabel, MasteryStatus } from "./masteryFixtures";

const statusStyles: Record<MasteryStatus, string> = {
  mastered: "bg-emerald-500",
  developing: "bg-violet-500",
  "needs-evidence": "bg-orange-500",
  "not-started": "bg-slate-300",
};

const StudentOverview = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  useEffect(() => {
    setTitle("Student Overview");
    setDescription("Each student is shown with the Unit that needs attention, grouped under its Domain and based on every recorded learning objective in that Unit.");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  const filteredStudents = useMemo(() => masteryStudents.filter((student) => {
    const query = searchTerm.trim().toLowerCase();
    const matchesSearch = !query || [student.name, student.email, student.batch, ...student.subjects]
      .some((value) => value.toLowerCase().includes(query));
    return matchesSearch
      && (statusFilter === "all" || student.status.toLowerCase() === statusFilter)
      && (batchFilter === "all" || student.batchId === batchFilter);
  }), [batchFilter, searchTerm, statusFilter]);

  const studentsNeedingEvidence = masteryStudents.filter((student) =>
    student.concepts.some((concept) => concept.objectives.some((objective) => objective.status === "needs-evidence")),
  ).length;

  const openRecord = (studentId: string, batchId: string) => {
    navigate(`${ROUTES.TUTOR_STUDENT_PERFORMANCE.path}?student=${studentId}&batch=${batchId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-xl border border-violet-100 bg-violet-50/60 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-950">Mastery watchlist</h2>
            <Badge variant="outline" className="border-violet-200 bg-white text-violet-700">Fixture preview</Badge>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Each snapshot is an exact view of the selected Unit's recorded learning objectives. Open the ledger to move from Domain to Unit, Lesson, and individual learning objective.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-orange-800">
          <AlertTriangle size={17} aria-hidden="true" />
          {studentsNeedingEvidence} students need an evidence check
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} aria-hidden="true" />
          <Input aria-label="Search students" placeholder="Search by student, subject, or batch" className="pl-10" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3 lg:flex">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full lg:w-44" aria-label="Filter by status"><Filter size={16} aria-hidden="true" /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
          </Select>
          <Select value={batchFilter} onValueChange={setBatchFilter}>
            <SelectTrigger className="w-full lg:w-64" aria-label="Filter by batch"><CalendarDays size={16} aria-hidden="true" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All batches</SelectItem>
              {Array.from(new Map(masteryStudents.map((student) => [student.batchId, student.batch])).entries()).map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <section aria-label="Assigned students" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="hidden grid-cols-[minmax(220px,1.2fr)_minmax(270px,1.5fr)_minmax(210px,1fr)_auto] gap-6 border-b border-slate-200 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 lg:grid">
          <span>Student</span><span>Priority Unit mastery</span><span>Next focus</span><span className="sr-only">Action</span>
        </div>
        {filteredStudents.map((student) => {
          const priority = student.concepts
            .flatMap((unit) => unit.objectives.map((objective) => ({ unit, objective })))
            .filter(({ objective }) => objective.status === "needs-evidence" || objective.status === "developing")
            .sort((a, b) => Number(b.objective.status === "needs-evidence") - Number(a.objective.status === "needs-evidence"))[0];
          const snapshotUnit = priority?.unit || student.concepts[0];
          const counts = snapshotUnit ? getUnitMasteryCounts(snapshotUnit) : { mastered: 0, developing: 0, "needs-evidence": 0, "not-started": 0 };
          const objectiveTotal = Object.values(counts).reduce((sum, count) => sum + count, 0);
          return (
            <article key={student.id} className="grid gap-5 border-b border-slate-100 px-5 py-5 last:border-b-0 lg:grid-cols-[minmax(220px,1.2fr)_minmax(270px,1.5fr)_minmax(210px,1fr)_auto] lg:items-center lg:gap-6">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100 font-semibold text-violet-700">{student.initials}</div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><h3 className="truncate font-semibold text-slate-950">{student.name}</h3><Badge variant="outline" className={student.status === "Active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-800"}>{student.status}</Badge></div>
                  <p className="mt-0.5 truncate text-sm text-slate-500">{student.grade} · {student.curriculum}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><BookOpen size={13} aria-hidden="true" /><span><span className="font-medium text-slate-600">Assigned curriculum:</span> {student.totalConcepts} units · {student.totalObjectives} objectives</span></p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium text-slate-700">{snapshotUnit ? `${snapshotUnit.strand} › ${snapshotUnit.name}` : "No Unit selected"}</p>
                <div className="mb-2 flex h-2.5 overflow-hidden rounded-full bg-slate-100" aria-label="Mastery distribution in the priority unit">
                  {(Object.keys(counts) as MasteryStatus[]).map((status) => counts[status] > 0 && <span key={status} className={statusStyles[status]} style={{ width: `${(counts[status] / Math.max(objectiveTotal, 1)) * 100}%` }} />)}
                </div>
                <p className="mb-1.5 text-xs text-slate-500">Mastery status across {objectiveTotal} learning objective{objectiveTotal === 1 ? "" : "s"} in this Unit.</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-4">
                  {(Object.keys(counts) as MasteryStatus[]).map((status) => <span key={status} className="flex items-center gap-1.5 text-slate-600"><span className={`h-2 w-2 rounded-full ${statusStyles[status]}`} /><strong className="tabular-nums text-slate-900">{counts[status]}</strong> {masteryStatusLabel[status]}</span>)}
                </div>
              </div>

              <div className="min-w-0">
                {priority ? <><p className="text-xs font-medium uppercase tracking-wide text-orange-700">{masteryStatusLabel[priority.objective.status]}</p><p className="mt-1 truncate text-sm font-medium text-slate-900">{priority.objective.lesson || priority.objective.code}</p><p className="mt-0.5 line-clamp-2 text-xs leading-5 text-slate-500">{priority.objective.improvement}</p></> : <p className="text-sm text-slate-500">No immediate intervention</p>}
              </div>

              <Button variant="outline" className="w-full justify-center gap-2 lg:w-auto" onClick={() => openRecord(student.id, student.batchId)}>Open ledger <ArrowRight size={16} aria-hidden="true" /></Button>
            </article>
          );
        })}
        {filteredStudents.length === 0 && <div className="px-6 py-14 text-center"><p className="font-medium text-slate-900">No students match these filters</p><p className="mt-1 text-sm text-slate-500">Try another name, status, or batch.</p></div>}
      </section>
    </div>
  );
};

export default StudentOverview;
